import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Camera, History, Home, Plus, Settings as SettingsIcon, TrendingUp, Users } from "lucide-react";

type Action = {
  id: string;
  label: string;
  keywords?: string;
  icon?: React.ComponentType<{ className?: string }>;
  run: () => void;
};

function isK(e: KeyboardEvent) {
  return e.key.toLowerCase() === "k";
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const actions = React.useMemo<Action[]>(
    () => [
      { id: "home", label: "Go to Dashboard", keywords: "home dashboard", icon: Home, run: () => navigate("/") },
      { id: "new", label: "New Session", keywords: "create start session", icon: Plus, run: () => navigate("/session/new") },
      { id: "import", label: "Import Session", keywords: "photo ocr", icon: Camera, run: () => navigate("/session/import") },
      { id: "sessions", label: "Sessions", keywords: "history list", icon: History, run: () => navigate("/sessions") },
      { id: "players", label: "Players", keywords: "roster", icon: Users, run: () => navigate("/players") },
      { id: "stats", label: "Statistics", keywords: "stats leaderboard", icon: TrendingUp, run: () => navigate("/statistics") },
      { id: "settings", label: "Settings", keywords: "prefs", icon: SettingsIcon, run: () => navigate("/settings") },
    ],
    [navigate]
  );

  const filtered = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return actions;
    return actions.filter((a) => {
      const hay = `${a.label} ${a.keywords ?? ""}`.toLowerCase();
      return hay.includes(s);
    });
  }, [actions, q]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && isK(e)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (!open) return;
      if (e.key === "Escape") setOpen(false);
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => Math.min(i + 1, filtered.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => Math.max(i - 1, 0));
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const a = filtered[active];
        if (!a) return;
        a.run();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active, filtered, open]);

  React.useEffect(() => {
    if (!open) return;
    setQ("");
    setActive(0);
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 overflow-hidden">
        <div className="border-b border-border p-3">
          <Input
            ref={inputRef}
            placeholder="Type a command…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-10"
          />
          <div className="mt-1 text-[11px] text-muted-foreground">
            ↑/↓ to select, Enter to run, Esc to close
          </div>
        </div>

        <div className="max-h-[320px] overflow-auto p-2">
          {filtered.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              No matches
            </div>
          ) : (
            <div className="grid gap-1">
              {filtered.map((a, i) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.id}
                    type="button"
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm",
                      i === active
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-muted"
                    )}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => {
                      a.run();
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {Icon && <Icon className="h-4 w-4 shrink-0" />}
                      <span className="truncate">{a.label}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      ⏎
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

